import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Image,
  Modal,
  TouchableOpacity,
} from "react-native";
import { useTranslation } from 'react-i18next';
import IdCard from "../../../../../../../../assets/images/id-card.png";
import GroupId from "../../../../../../../../assets/images/group-id.png";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export default function ReceiverFullNameStep({ onValidationChange }: Props) {
  const { t } = useTranslation();
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const destinationInfoFields: any[] = (gffpFieldsByCategory?.["Destination Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = destinationInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    const baseLabel = field?.FieldLabel ?? field?.fieldLabel;
    const visibility = field?.Visibility ?? field?.visibility;
    const isOptional = visibility === 1 || visibility === "OPT" || visibility === "1";
    return baseLabel ? `${baseLabel}${isOptional ? " (optional)" : ""}` : undefined;
  };

  // Bind to store
  const storeFirst = useSessionStore((s: any) => s.receiverFirstName);
  const storeMiddle = useSessionStore((s: any) => s.receiverMiddleName);
  const storeLast = useSessionStore((s: any) => s.receiverLastName);
  const storeLast2 = useSessionStore((s: any) => s.receiverLastName2);
  const setFirst = useSessionStore((s: any) => s.setReceiverFirstName);
  const setMiddle = useSessionStore((s: any) => s.setReceiverMiddleName);
  const setLast = useSessionStore((s: any) => s.setReceiverLastName);
  const setLast2 = useSessionStore((s: any) => s.setReceiverLastName2);

  const [firstName, setFirstName] = useState(storeFirst ?? "");
  const [middleName, setMiddleName] = useState(storeMiddle ?? "");
  const [lastName, setLastName] = useState(storeLast ?? "");
  const [lastName2, setLastName2] = useState(storeLast2 ?? "");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => { setFirst?.(firstName || null); }, [firstName]);
  useEffect(() => { setMiddle?.(middleName || null); }, [middleName]);
  useEffect(() => { setLast?.(lastName || null); }, [lastName]);
  useEffect(() => { setLast2?.(lastName2 || null); }, [lastName2]);

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <Text style={styles.title}>{t("TELL_US_ABOUT_RECEIVER")}</Text>
      <Text style={styles.subtitle}>{t("PROVIDE_DETAILS_ABOUT_YOUR_RECEIVER")}</Text>

      <FloatingTextInputComponent
            testID="receiverFirstName"
            label={getFieldLabel("receiverFirstName") || t("FIRST_NAME")}
            value={firstName}
            onChangeText={setFirstName}
      />
      <FloatingTextInputComponent
            testID="receiverMiddleName"
            label={getFieldLabel("receiverMiddleName") || t("MIDDLE_NAME")}
            value={middleName}
            onChangeText={setMiddleName}
          />
      <FloatingTextInputComponent
            testID="receiverLastName"
            label={getFieldLabel("receiverLastName") || t("LAST_NAME")}
            value={lastName}
            onChangeText={setLastName}
          />
      <FloatingTextInputComponent
            testID="receiverLastName2"
            label={getFieldLabel("receiverLastName2") || t("LAST_NAME")}
            value={lastName2}
            onChangeText={setLastName2}
          />
      <View style={styles.hintBox}>
        <Image
          source={IdCard}
          style={{height: 24, width: 24, marginHorizontal: 16}}        
        />
        <Text style={styles.hintText}>
          {t("YOUR_RECEIVER_NAME_MUST_MATCH")}
        </Text>
      </View>
       {/* Bottom Popup Confirmation */}
            <Modal
              visible={showConfirmModal}
              animationType="slide"
              transparent={true}
            >
              <View style={styles.modalOverlay}>
                <View style={styles.bottomSheet}>
                  {/* Header Icon */}
                  <View style={styles.iconContainer}>
                    <View style={styles.iconCircle}>
                      <Image
                        source={GroupId}
                        style={{ height: 80, width: 80}}
                      />
                    </View>
                  </View>
                  <Text style={styles.modalTitle}>{t("IS_THIS_THEIR_LEGAL_NAME")}</Text>
                  <Text style={styles.modalText}>
                    {t("MAKE_SURE_THEIR_NAME_MATCHES")}
                  </Text>
                  <Text style={styles.modalDOB}>Jon Dee</Text>
      
                  <TouchableOpacity
                    style={styles.confirmBtn}
                    onPress={() => setShowConfirmModal(false)}
                  >
                    <Text style={styles.confirmText}>{t("CONFIRM")}</Text>
                  </TouchableOpacity>
      
                  <TouchableOpacity
                    style={styles.cancelBtn}
                    onPress={() => setShowConfirmModal(false)}
                  >
                    <Text style={styles.cancelText}>{t("EDIT")}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  input: {
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 16,
    marginBottom: 14,
    color: "#737373"
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    alignItems: 'center',
    backgroundColor: "#fff",
    padding: 20,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 20
  },
  modalText: {
    fontSize: 15,
    color: "#555",
    fontWeight: "400",
    width: 311,
    paddingHorizontal: 15,
    paddingBottom: 20,
    textAlign: 'center'
  },
  modalDOB: {
    fontSize: 32,
    fontWeight: "500",
    marginBottom: 20,
    textAlign: "center",
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    width: 350
  },
  confirmText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 16
  },
  cancelBtn: {
    marginTop: 20,
    alignItems: "center"
  },
  cancelText: {
    fontSize: 15,
    color: "#666"
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 30,
    paddingTop: 80,
    paddingBottom: 50,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#2196F3',
  },
  iconText: {
    fontSize: 32,
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 12,
    marginTop: 32,
    backgroundColor: Colors.white,
    marginBottom: 79
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400', 
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingRight: 50
  },
});